import { useCallback, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";

type SiteType = "ecommerce" | "booking" | "ecommerce+booking" | "landing";

interface NavItem {
  readonly label: string;
  readonly href: string;
  readonly icon: string;
  readonly minRole: string;
}

interface SidebarGroup {
  readonly label: string;
  readonly items: ReadonlyArray<NavItem>;
  readonly module?: "ecommerce" | "booking" | undefined;
  readonly superAdminOnly?: true | undefined;
}

const SIDEBAR_GROUPS: ReadonlyArray<SidebarGroup> = [
  {
    label: "ראשי",
    items: [
      { label: "דשבורד", href: "/", icon: "📊", minRole: "viewer" },
    ],
  },
  {
    label: "ניהול חנות",
    module: "ecommerce",
    items: [
      { label: "הזמנות", href: "/orders", icon: "📦", minRole: "viewer" },
      { label: "מוצרים", href: "/products", icon: "🌸", minRole: "viewer" },
      { label: "קופונים", href: "/coupons", icon: "🎟️", minRole: "employee" },
      { label: "ביקורות", href: "/reviews", icon: "⭐", minRole: "employee" },
      { label: "סבסקריפציות", href: "/subscriptions", icon: "🔄", minRole: "employee" },
      { label: "חלונות קופצים", href: "/popups", icon: "🎯", minRole: "manager" },
    ],
  },
  {
    label: "תורים",
    module: "booking",
    items: [
      { label: "לוח שנה", href: "/booking/calendar", icon: "📅", minRole: "viewer" },
      { label: "תורים", href: "/booking/appointments", icon: "⏰", minRole: "viewer" },
      { label: "שירותים", href: "/booking/services", icon: "🛎️", minRole: "employee" },
      { label: "הגדרות זמינות", href: "/booking/settings", icon: "⚙️", minRole: "manager" },
    ],
  },
  {
    label: "שיווק",
    module: "ecommerce",
    items: [
      { label: "פוקסי AI", href: "/foxy", icon: "💬", minRole: "viewer" },
    ],
  },
  {
    label: "הגדרות",
    items: [
      { label: "כללי", href: "/settings", icon: "⚙️", minRole: "manager" },
      { label: "צוות", href: "/team", icon: "👥", minRole: "manager" },
      { label: "משפטי", href: "/legal", icon: "⚖️", minRole: "manager" },
      { label: "חיוב", href: "/billing", icon: "💳", minRole: "owner" },
    ],
  },
  {
    label: "ניהול פלטפורמה",
    superAdminOnly: true,
    items: [
      { label: "דשבורד ראשי", href: "/super/dashboard", icon: "🛡️", minRole: "owner" },
      { label: "דיירים", href: "/super/tenants", icon: "🏢", minRole: "owner" },
      { label: "הוספת דייר", href: "/super/provisioning", icon: "➕", minRole: "owner" },
      { label: "בריאות", href: "/super/health", icon: "💚", minRole: "owner" },
      { label: "תמיכה", href: "/super/support", icon: "🎧", minRole: "owner" },
      { label: "חיובים", href: "/super/billing", icon: "🧾", minRole: "owner" },
      { label: "שותפים", href: "/super/partners", icon: "🤝", minRole: "owner" },
      { label: "גיבויים", href: "/super/backups", icon: "💾", minRole: "owner" },
    ],
  },
];

function getSiteType(): SiteType {
  try {
    const stored = localStorage.getItem("site_type");
    if (stored === "ecommerce" || stored === "booking" || stored === "ecommerce+booking" || stored === "landing") {
      return stored;
    }
  } catch { /* localStorage unavailable */ }
  return "ecommerce+booking";
}

function isGroupVisible(group: SidebarGroup, siteType: SiteType): boolean {
  if (!group.module) return true;
  if (siteType === "ecommerce+booking") return true;
  if (siteType === "landing") return false;
  return group.module === siteType;
}

function getStoredCollapsed(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem("sidebar-collapsed");
    if (stored !== null) return JSON.parse(stored) as Record<string, boolean>;
  } catch { /* Invalid JSON */ }
  return {};
}

function storeCollapsed(state: Record<string, boolean>): void {
  try { localStorage.setItem("sidebar-collapsed", JSON.stringify(state)); } catch { /* Storage full */ }
}

interface SidebarProps {
  readonly collapsed?: boolean;
}

export function Sidebar({ collapsed = false }: SidebarProps): ReactNode {
  const { hasRole, user, logout } = useAuth();
  const location = useLocation();
  const [groupCollapsed, setGroupCollapsed] = useState<Record<string, boolean>>(getStoredCollapsed);

  const toggleGroup = useCallback((label: string) => {
    setGroupCollapsed((prev) => {
      const next = { ...prev, [label]: !(prev[label] ?? false) };
      storeCollapsed(next);
      return next;
    });
  }, []);

  const siteType = getSiteType();
  const isSuperAdmin = user?.tenantId === "platform";
  const filteredGroups = SIDEBAR_GROUPS.filter(
    (group) => isGroupVisible(group, siteType) && (!group.superAdminOnly || isSuperAdmin),
  )
    .map((group) => ({ ...group, items: group.items.filter((item) => hasRole(item.minRole)) }))
    .filter((group) => group.items.length > 0);

  return (
    <nav
      className={cn("flex h-full flex-col", collapsed ? "w-16" : "w-[272px]")}
      style={{ background: "linear-gradient(180deg, #0F0A1E 0%, #1A1035 50%, #0F172A 100%)" }}
    >
      <div
        className={cn("flex h-16 shrink-0 items-center border-b", collapsed ? "justify-center px-3" : "gap-3 px-5")}
        style={{ borderColor: "rgba(255,255,255,0.07)" }}
      >
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg shadow-lg"
          style={{ background: "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)", boxShadow: "0 4px 14px rgba(124,58,237,0.4)" }}
        >
          🦊
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm leading-tight truncate" style={{ fontFamily: "Rubik, sans-serif", fontWeight: 900, color: "#fff" }}>
              Foxy Admin
            </p>
            <p className="text-[11px] leading-tight" style={{ color: "rgba(255,255,255,0.45)" }}>
              ניהול העסק
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-2" style={{ scrollbarColor: "rgba(255,255,255,0.1) transparent" }}>
        {filteredGroups.map((group, groupIndex) => {
          const isCollapsedGroup = groupCollapsed[group.label] ?? false;
          return (
            <div key={group.label} className={groupIndex > 0 ? "mt-3" : ""}>
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="flex w-full items-center justify-between px-3 py-1 mb-1 cursor-pointer rounded-md transition-colors"
                  style={{ fontFamily: "Rubik, sans-serif", fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "rgba(255,255,255,0.35)" }}
                >
                  <span>{group.label}</span>
                  <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", isCollapsedGroup && "-rotate-90")} />
                </button>
              )}
              {!isCollapsedGroup && group.items.map((item) => {
                const isActive = item.href === "/" ? location.pathname === "/" || location.pathname === "" : location.pathname.startsWith(item.href);
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={cn("relative flex items-center gap-3 rounded-lg px-3 py-[0.72rem] mb-[3px] transition-all duration-200", collapsed && "justify-center px-0")}
                    style={isActive ? { background: "rgba(124,58,237,0.15)", color: "#fff", boxShadow: "0 0 12px rgba(124,58,237,0.15)" } : { color: "rgba(255,255,255,0.6)" }}
                    onMouseEnter={(e) => { if (!isActive) { (e.currentTarget as HTMLElement).style.background = "rgba(124,58,237,0.1)"; (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.9)"; } }}
                    onMouseLeave={(e) => { if (!isActive) { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.6)"; } }}
                  >
                    {isActive && !collapsed && (
                      <span className="absolute end-0 top-1/2 -translate-y-1/2 w-[3px] rounded-s-full" style={{ height: "60%", background: "linear-gradient(180deg, #7C3AED 0%, #EC4899 100%)" }} />
                    )}
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base leading-none" style={isActive ? { background: "rgba(124,58,237,0.25)" } : {}}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <span className="truncate" style={{ fontFamily: "Rubik, sans-serif", fontWeight: 500, fontSize: "0.92rem" }}>
                        {item.label}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="shrink-0 px-2 py-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <button
          type="button"
          onClick={() => void logout()}
          className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-[0.72rem] transition-all duration-200", collapsed && "justify-center px-0")}
          style={{ color: "rgba(255,255,255,0.4)" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.12)"; (e.currentTarget as HTMLElement).style.color = "rgba(252,165,165,0.9)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.4)"; }}
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base leading-none">
            <LogOut className="h-4 w-4" />
          </span>
          {!collapsed && (
            <span style={{ fontFamily: "Rubik, sans-serif", fontWeight: 500, fontSize: "0.92rem" }}>
              התנתקות
            </span>
          )}
        </button>
      </div>
    </nav>
  );
}

export { SIDEBAR_GROUPS };
export type { SidebarGroup, NavItem };
